/**
 * Mist language definition for Monaco Editor.
 *
 * Derived from the real rivellum-mist-parser lexer (TokenKind enum)
 * and parser grammar. This is NOT guessed — every keyword, operator,
 * and type maps directly to the Rust lexer/parser crate.
 */
import type * as Monaco from 'monaco-editor';

export const MIST_LANGUAGE_ID = 'mist';

/* ------------------------------------------------------------------ */
/*  Language configuration (brackets, comments, auto-close, etc.)     */
/* ------------------------------------------------------------------ */
export const mistLanguageConfiguration: Monaco.languages.LanguageConfiguration = {
  comments: { lineComment: '#' },
  brackets: [
    ['(', ')'],
    ['[', ']'],
    ['{', '}'],
  ],
  autoClosingPairs: [
    { open: '(', close: ')' },
    { open: '[', close: ']' },
    { open: '{', close: '}' },
    { open: '"', close: '"', notIn: ['string'] },
    { open: "'", close: "'", notIn: ['string'] },
  ],
  surroundingPairs: [
    { open: '(', close: ')' },
    { open: '[', close: ']' },
    { open: '{', close: '}' },
    { open: '"', close: '"' },
    { open: "'", close: "'" },
  ],
  indentationRules: {
    increaseIndentPattern: /^\s*(contract|function|if|else|for|while|policy)\b.*$/,
    decreaseIndentPattern: /^\s*(else)\b/,
  },
  wordPattern: /(-?\d*\.\d\w*)|([^`~!@#%^&*()\-=+[{\]}\\|;:'",.<>/?\s]+)/,
  onEnterRules: [
    {
      // After lines ending with a block-starter keyword (no colon needed in Mist)
      beforeText: /^\s*(contract|function|if|else|for|while|policy)\b.*$/,
      action: { indentAction: 2 /* IndentAction.Indent */ },
    },
  ],
};

/* ------------------------------------------------------------------ */
/*  Monarch tokenizer                                                 */
/* ------------------------------------------------------------------ */

/**
 * All keywords from rivellum-mist-parser/src/lexer.rs `identifier_to_keyword()`.
 */
const KEYWORDS = [
  'contract', 'state', 'function', 'event', 'let', 'if', 'else',
  'for', 'while', 'return', 'require', 'assert', 'emit', 'policy', 'in',
  'can_mint', 'can_burn',
];

const VALUE_OPS = [
  'hold', 'pay', 'release', 'refund', 'split', 'mint', 'burn',
];

const INTRINSICS = ['now', 'oracle', 'caller', 'intent'];

const TYPES = [
  'Int', 'Bool', 'String', 'Address', 'Timestamp', 'Value',
  'U128', 'U64', 'Asset', 'VaultRef',
  'List', 'Set', 'Map',
];

const VALUE_OP_MODIFIERS = [
  'asset', 'from', 'to', 'into', 'vault', 'authority',
];

export const mistTokensProvider: Monaco.languages.IMonarchLanguage = {
  defaultToken: 'invalid',
  tokenPostfix: '.mist',

  keywords: KEYWORDS,
  valueOps: VALUE_OPS,
  intrinsics: INTRINSICS,
  typeKeywords: TYPES,
  valueOpModifiers: VALUE_OP_MODIFIERS,
  booleans: ['true', 'false'],

  operators: [
    '==', '!=', '<=', '>=', '&&', '||', '->', '!',
    '+', '-', '*', '/', '%', '=', '<', '>', '.',
  ],

  symbols: /[=><!~?:&|+\-*/^%]+/,

  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,

  tokenizer: {
    root: [
      // Comments
      [/#.*$/, 'comment'],

      // Whitespace
      [/\s+/, 'white'],

      // Strings
      [/"([^"\\]|\\.)*$/, 'string.invalid'], // unterminated
      [/'([^'\\]|\\.)*$/, 'string.invalid'], // unterminated
      [/"/, 'string', '@string_double'],
      [/'/, 'string', '@string_single'],

      // Numbers
      [/\d+/, 'number'],

      // Identifiers and keywords
      [/[a-zA-Z_]\w*/, {
        cases: {
          '@keywords': 'keyword',
          '@valueOps': 'keyword.value-op',
          '@intrinsics': 'keyword.intrinsic',
          '@typeKeywords': 'type',
          '@valueOpModifiers': 'keyword.modifier',
          '@booleans': 'constant.boolean',
          '@default': 'identifier',
        },
      }],

      // Operators
      [/->/, 'operator.arrow'],
      [/[=><!~?:&|+\-*/^%]+/, {
        cases: {
          '@operators': 'operator',
          '@default': '',
        },
      }],

      // Delimiters
      [/[()[\]{}]/, '@brackets'],
      [/,/, 'delimiter'],
    ],

    string_double: [
      [/[^\\"]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, 'string', '@pop'],
    ],

    string_single: [
      [/[^\\']+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, 'string', '@pop'],
    ],
  },
};

/* ------------------------------------------------------------------ */
/*  Theme tokens                                                      */
/* ------------------------------------------------------------------ */
export const mistThemeRules: Monaco.editor.ITokenThemeRule[] = [
  { token: 'keyword.mist', foreground: 'C586C0' },
  { token: 'keyword.value-op.mist', foreground: '4EC9B0' },
  { token: 'keyword.intrinsic.mist', foreground: 'DCDCAA' },
  { token: 'keyword.modifier.mist', foreground: '9CDCFE' },
  { token: 'type.mist', foreground: '4EC9B0' },
  { token: 'constant.boolean.mist', foreground: '569CD6' },
  { token: 'comment.mist', foreground: '6A9955' },
  { token: 'string.mist', foreground: 'CE9178' },
  { token: 'string.escape.mist', foreground: 'D7BA7D' },
  { token: 'number.mist', foreground: 'B5CEA8' },
  { token: 'operator.mist', foreground: 'D4D4D4' },
  { token: 'operator.arrow.mist', foreground: 'C586C0' },
  { token: 'identifier.mist', foreground: '9CDCFE' },
];

/* ------------------------------------------------------------------ */
/*  Registration helper                                               */
/* ------------------------------------------------------------------ */
export function registerMistLanguage(monaco: typeof Monaco): void {
  // Guard against double-registration
  const langs = monaco.languages.getLanguages();
  if (langs.some(l => l.id === MIST_LANGUAGE_ID)) return;

  monaco.languages.register({
    id: MIST_LANGUAGE_ID,
    extensions: ['.mist'],
    aliases: ['Mist', 'mist'],
    mimetypes: ['text/x-mist'],
  });

  monaco.languages.setLanguageConfiguration(MIST_LANGUAGE_ID, mistLanguageConfiguration);
  monaco.languages.setMonarchTokensProvider(MIST_LANGUAGE_ID, mistTokensProvider);

  // Extend the VS Dark theme with Mist-specific tokens
  monaco.editor.defineTheme('rivellum-dark', {
    base: 'vs-dark',
    inherit: true,
    rules: mistThemeRules,
    colors: {
      'editor.background': '#0d1117',
      'editor.foreground': '#e6edf3',
      'editorLineNumber.foreground': '#484f58',
      'editorLineNumber.activeForeground': '#e6edf3',
      'editor.selectionBackground': '#264f78',
      'editor.lineHighlightBackground': '#161b22',
      'editorCursor.foreground': '#9b59b6',
      'editorWidget.background': '#161b22',
      'editorSuggestWidget.background': '#161b22',
      'editorHoverWidget.background': '#161b22',
    },
  });
}
